import { ChangeDetectionStrategy, Component, computed, HostListener, input, output } from '@angular/core';
import { DateAdapter } from '../adapters/abstract-date-adapter';
import { HxCalendarBody } from './calendar-body';
import { CalendarCell } from './calendar-cell';

/** Month view component displaying days in a calendar grid. Delegates rendering to CalendarBody. */
@Component({
  selector: 'hx-month-view',
  standalone: true,
  imports: [HxCalendarBody],
  template: `
    <hx-calendar-body
      [activeCell]="activeCellIndex()"
      [cellAspectRatio]="1"
      [label]="monthLabel()"
      [numCols]="7"
      [rangeEnd]="rangeEndIndex()"
      [rangeStart]="rangeStartIndex()"
      [rows]="dateRows()"
      [selectedCell]="selectedCellIndex()"
      [weekdayLabels]="weekdayLabels()"
      (activeDateChange)="activeDateChange.emit($event)"
      (selectedChange)="selectedChange.emit($event)" />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxMonthView<D = Date> {
  readonly dateAdapter = input.required<DateAdapter<D>>();
  readonly activeDate = input.required<D>();
  readonly selected = input<D | null>(null);
  readonly minDate = input<D | null>(null);
  readonly maxDate = input<D | null>(null);
  readonly dateFilter = input<((date: D) => boolean) | null>(null);
  readonly rangeStart = input<D | null>(null);
  readonly rangeEnd = input<D | null>(null);

  readonly selectedChange = output<D>();
  readonly activeDateChange = output<D>();

  protected readonly monthLabel = computed(() => {
    const adapter = this.dateAdapter();
    const active = this.activeDate();
    const monthNames = adapter.getMonthNames('long');
    return `${monthNames[adapter.getMonth(active)]} ${adapter.getYear(active)}`;
  });

  protected readonly weekdayLabels = computed(() => {
    return this.dateAdapter().getDayOfWeekNames('narrow');
  });

  protected readonly dateRows = computed(() => {
    const adapter = this.dateAdapter();
    const active = this.activeDate();
    const year = adapter.getYear(active);
    const month = adapter.getMonth(active);

    const firstDateOfMonth = adapter.getFirstDateOfMonth(active);
    const daysInMonth = adapter.getNumDaysInMonth(active);
    const firstDayOfWeek = adapter.getFirstDayOfWeek();
    const startingDayOffset = (adapter.getDayOfWeek(firstDateOfMonth) - firstDayOfWeek + 7) % 7;

    const rows: CalendarCell<D>[][] = [];
    let currentRow: CalendarCell<D>[] = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDayOffset; i++) {
      const date = adapter.create(year, month, 1 - startingDayOffset + i);
      currentRow.push({
        value: date,
        displayValue: '',
        ariaLabel: '',
        enabled: false,
        cssClasses: ['hx-calendar-cell--outside-month', 'hx-calendar-cell--hidden'],
      });
    }

    // Add cells for each day of the current month
    for (let dayIndex = 1; dayIndex <= daysInMonth; dayIndex++) {
      const date = adapter.create(year, month, dayIndex);
      const enabled = this.isDateEnabled(date);

      const isToday = adapter.sameDate(date, adapter.today());
      const cssClasses: string[] = [];
      if (isToday) {
        cssClasses.push('hx-calendar-body__cell--today');
      }

      currentRow.push({
        value: date,
        displayValue: adapter.getDate(date).toString(),
        ariaLabel: adapter.format(date, 'MMMM D, YYYY'),
        enabled: enabled,
        cssClasses,
      });

      if (currentRow.length === 7) {
        rows.push(currentRow);
        currentRow = [];
      }
    }

    // Add empty cells for remaining days to complete the last row
    if (currentRow.length > 0) {
      const remainingCells = 7 - currentRow.length;
      for (let i = 0; i < remainingCells; i++) {
        const date = adapter.create(year, month, daysInMonth + i + 1);
        currentRow.push({
          value: date,
          displayValue: '',
          ariaLabel: '',
          enabled: false,
          cssClasses: ['hx-calendar-cell--outside-month', 'hx-calendar-cell--hidden'],
        });
      }
      rows.push(currentRow);
    }

    return rows;
  });

  protected readonly activeCellIndex = computed(() => {
    const adapter = this.dateAdapter();
    const active = this.activeDate();
    const rows = this.dateRows();

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]!;
      for (let j = 0; j < row.length; j++) {
        if (adapter.sameDate(row[j]!.value, active)) {
          return i * 7 + j;
        }
      }
    }

    return 0;
  });

  protected readonly selectedCellIndex = computed(() => {
    const selected = this.selected();
    if (!selected) {
      return null;
    }

    const adapter = this.dateAdapter();
    const rows = this.dateRows();

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]!;
      for (let j = 0; j < row.length; j++) {
        if (adapter.sameDate(row[j]!.value, selected)) {
          return i * 7 + j;
        }
      }
    }

    return null;
  });

  protected readonly rangeStartIndex = computed(() => {
    const start = this.rangeStart();
    if (!start) {
      return null;
    }

    const adapter = this.dateAdapter();
    const rows = this.dateRows();

    // Find the first visible (non-hidden) date in the month
    let firstVisibleDate: D | null = null;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]!;
      for (let j = 0; j < row.length; j++) {
        const cell = row[j]!;
        if (!cell.cssClasses.includes('hx-calendar-cell--hidden')) {
          firstVisibleDate = cell.value;
          break;
        }
      }
      if (firstVisibleDate) break;
    }

    // Check if start date is before the current month view
    if (firstVisibleDate && adapter.compareDate(start, firstVisibleDate) < 0) {
      // Start is before this month - don't show dark green circle
      // Return special marker to indicate range should start from beginning
      return -1;
    }

    return this.findDateIndex(start);
  });

  protected readonly rangeEndIndex = computed(() => {
    const end = this.rangeEnd();
    if (!end) {
      return null;
    }

    const adapter = this.dateAdapter();
    const rows = this.dateRows();

    // Find the last visible (non-hidden) date in the month
    let lastVisibleDate: D | null = null;
    for (let i = rows.length - 1; i >= 0; i--) {
      const row = rows[i]!;
      for (let j = row.length - 1; j >= 0; j--) {
        const cell = row[j]!;
        if (!cell.cssClasses.includes('hx-calendar-cell--hidden')) {
          lastVisibleDate = cell.value;
          break;
        }
      }
      if (lastVisibleDate) break;
    }

    // Check if end date is after the current month view
    if (lastVisibleDate && adapter.compareDate(end, lastVisibleDate) > 0) {
      // End is after this month - don't show dark green circle
      // Return special marker to indicate range should extend to end
      return -1;
    }

    return this.findDateIndex(end);
  });

  private findDateIndex(date: D): number | null {
    const adapter = this.dateAdapter();
    const rows = this.dateRows();

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]!;
      for (let j = 0; j < row.length; j++) {
        if (adapter.sameDate(row[j]!.value, date)) {
          return i * 7 + j;
        }
      }
    }

    return null;
  }

  private isDateEnabled(date: D): boolean {
    const adapter = this.dateAdapter();
    const min = this.minDate();
    const max = this.maxDate();
    const filter = this.dateFilter();

    if (min && adapter.compareDate(date, min) < 0) {
      return false;
    }
    if (max && adapter.compareDate(date, max) > 0) {
      return false;
    }
    if (filter && !filter(date)) {
      return false;
    }

    return true;
  }

  /** Handle keyboard navigation within the month view. */
  @HostListener('keydown', ['$event'])
  protected handleKeydown(event: KeyboardEvent): void {
    const adapter = this.dateAdapter();
    const active = this.activeDate();
    let newDate: D | null = null;

    switch (event.key) {
      // Arrow navigation
      case 'ArrowLeft':
        newDate = adapter.addCalendarDays(active, -1);
        break;
      case 'ArrowRight':
        newDate = adapter.addCalendarDays(active, 1);
        break;
      case 'ArrowUp':
        newDate = adapter.addCalendarDays(active, -7);
        break;
      case 'ArrowDown':
        newDate = adapter.addCalendarDays(active, 7);
        break;

      // Month navigation
      case 'Home':
        newDate = adapter.getFirstDateOfMonth(active);
        break;
      case 'End':
        newDate = adapter.getLastDateOfMonth(active);
        break;
      case 'PageUp':
        newDate = event.altKey ? adapter.addCalendarYears(active, -1) : adapter.addCalendarMonths(active, -1);
        break;
      case 'PageDown':
        newDate = event.altKey ? adapter.addCalendarYears(active, 1) : adapter.addCalendarMonths(active, 1);
        break;

      // Selection
      case 'Enter':
      case ' ':
        this.selectedChange.emit(active);
        event.preventDefault();
        event.stopPropagation();
        return;

      default:
        return;
    }

    if (newDate) {
      // Validate against min/max boundaries
      const min = this.minDate();
      const max = this.maxDate();

      if (min && adapter.compareDate(newDate, min) < 0) {
        newDate = min;
      }
      if (max && adapter.compareDate(newDate, max) > 0) {
        newDate = max;
      }

      this.activeDateChange.emit(newDate);
      event.preventDefault();
    }
  }
}
